import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, LogOut, FileText, Clock, CheckCircle2, Download } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Eyebrow } from "../components/site/Reveal";
import { BrochureButton } from "../components/site/BrochureButton";
import { ChangePasswordDialog } from "../components/site/ChangePasswordDialog";

const StatusBadge = ({ status }) =>
  status === "sent" ? (
    <span className="inline-flex items-center gap-1 bg-[var(--forest)] px-2 py-1 font-mono text-xs uppercase text-white"><CheckCircle2 className="h-3 w-3" /> Sent</span>
  ) : (
    <span className="inline-flex items-center gap-1 bg-[var(--sand)] px-2 py-1 font-mono text-xs uppercase"><Clock className="h-3 w-3" /> In progress</span>
  );

const downloadCert = async (id) => {
  try {
    const res = await api.get(`/certificate-requests/${id}/certificate`, { responseType: "blob" });
    const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "work-done-certificate";
    const url = URL.createObjectURL(res.data);
    Object.assign(document.createElement("a"), { href: url, download: name }).click();
    URL.revokeObjectURL(url);
  } catch (err) {
    const detail = err.response?.data instanceof Blob ? JSON.parse(await err.response.data.text()).detail : err.response?.data?.detail;
    toast.error(formatApiError(detail));
  }
};

const MyRequests = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/certificate-requests/mine").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (rows === null) return <Loader2 className="h-6 w-6 animate-spin" />;
  if (!rows.length) return (
    <p data-testid="account-requests-empty" className="text-[var(--muted-ink)]">
      No requests yet. Open the <Link to="/#works" className="font-semibold text-[var(--rust)] underline">works register</Link> and choose "Request certificate".
    </p>
  );
  return (
    <ul className="divide-y divide-black/10 border-y border-black/10">
      {rows.map((r) => (
        <li key={r.id} data-testid="account-request-row" className="flex flex-col justify-between gap-3 py-5 md:flex-row md:items-center">
          <div>
            <p className="font-semibold">{r.work_title}</p>
            <p className="font-mono text-xs text-[var(--muted-ink)]">Requested {new Date(r.created_at).toLocaleDateString("en-IN")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge status={r.status} />
            {r.can_download && (
              <button type="button" onClick={() => downloadCert(r.id)} data-testid={`account-download-cert-${r.id}`}
                className="btn-rust inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 text-xs"><Download className="h-3.5 w-3.5" /> Download certificate</button>
            )}
            {r.status === "sent" && !r.can_download && <span className="text-xs text-[var(--muted-ink)]">Sent by email</span>}
          </div>
        </li>
      ))}
    </ul>
  );
};

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (user === null) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!user) return <Navigate to="/login?next=/account" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;

  return (
    <main data-testid="account-page" className="min-h-screen bg-[var(--paper)] pb-24 pt-32">
      <title>My account | Vata Infrastructures</title>
      <meta name="robots" content="noindex" />
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Eyebrow>My account</Eyebrow>
            <h1 data-testid="account-name" className="section-title mt-4">Hello, {user.name.split(" ")[0]}</h1>
            <p className="mt-3 text-[var(--muted-ink)]">{user.organisation} · {user.email} · {user.phone}</p>
          </div>
          <div className="flex items-center gap-6">
            <ChangePasswordDialog />
            <button type="button" onClick={() => { navigate("/"); logout(); }} data-testid="account-logout-button" className="inline-flex items-center gap-2 text-sm font-semibold uppercase"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
        <div className="mt-16 grid gap-12 lg:grid-cols-12">
          <section className="lg:col-span-8" data-testid="account-requests">
            <h2 className="font-display text-3xl font-extrabold uppercase">Certificate requests</h2>
            <div className="mt-6"><MyRequests /></div>
          </section>
          <aside className="lg:col-span-4" data-testid="account-documents">
            <div className="bg-[var(--ink)] p-8 text-white">
              <FileText className="h-8 w-8 text-[var(--rust-light)]" />
              <h2 className="mt-4 font-display text-3xl font-extrabold uppercase">Documents</h2>
              <p className="mt-2 text-sm text-white/70">Company profile with works, projects and registrations.</p>
              <BrochureButton className="mt-6 w-full justify-center py-4" testid="account-brochure-button" />
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
